import { BehaviorSubject, Observable } from 'rxjs';

import { UserAccessLevel } from 'src/app/core/services/user.service';
import { AppFeatures } from 'src/app/core/app.features';
import { AppState } from 'src/app/reducers';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  DataObject,
  ObjectPermission,
  User,
} from 'src/app/core/models/core.models';
import { map, tap } from 'rxjs/operators';
import { selectUser } from 'src/app/core/reducers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class UserFacadeService {
  // BehaviorSubject caches the current user value
  private currentUserSubject = new BehaviorSubject<User | null>(null);

  private destroyRef = inject(DestroyRef);

  // Expose user$ as an observable for async use cases
  user$: Observable<User | null> = this.currentUserSubject.asObservable();

  constructor(private store: Store<AppState>) {
    // Subscribe to the store and update the BehaviorSubject with the latest user
    this.store
      .select(selectUser)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((user) => this.currentUserSubject.next(user)),
      )
      .subscribe();
  }

  /**
   * Synchronous USER_ACCESS_LEVEL check against the cached user.
   *
   * @param level the minimum level required
   * @return true when the current user's level is at or above it
   */
  hasMinAccessLevelSync(level: UserAccessLevel): boolean {
    const current = this.currentUserSubject.value?.user_access_level?.id;
    return typeof current === 'number' && current >= level;
  }

  // Synchronous permission check using the cached value
  hasPermissionSync(appFeature: AppFeatures): boolean {
    const user = this.currentUserSubject.value;
    return user?.feature_components?.[appFeature] === true;
  }

  // Asynchronous permission check returning an observable
  hasPermissionAsync(appFeature: AppFeatures): Observable<boolean> {
    return this.user$.pipe(
      map((user) => user?.feature_components?.[appFeature] === true),
    );
  }

  hasAllObjectPermissionsSync(
    object: DataObject,
    req: ObjectPermission[],
  ): boolean {
    return object && (!req || req.every((k) => !!object[k]));
  }
}
